import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { KPI } from '@/components/ui/kpi';
import { CompactKpiCard } from '@/components/kpi-card';
import { Badge } from '@/components/ui/badge';
import { StatusIndicator } from '@/components/ui/status-indicator';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { SensitiveData } from '@/components/sensitive-data';
import PageHeaderBlock from '@/registry/evo-shadcn/blocks/page-header';
import { DataTableEnhanced, cycleColumnSort } from '@/components/ui/data-table-enhanced';
import type { ColumnDef, ColumnSort } from '@/components/ui/data-table-enhanced';
import {
  ProjectFilterSheet,
  applyColumnFilters,
  countActiveFilters,
} from '@/components/project-filter-sheet';
import type { ColumnFilters } from '@/components/project-filter-sheet';
import type { ColumnDefinition } from '@/components/column-configurator-drawer';
import { exportProjectsToExcel, fetchAllProjectPages } from '@/lib/export-projects-excel';
import type { ExportReferenceData } from '@/lib/export-projects-excel';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Search,
  Filter,
  Download,
  Loader2,
  X,
  LayoutGrid,
  CircleCheck,
  TriangleAlert,
  CircleX,
} from 'lucide-react';
import { fetchProjects, fetchClients, fetchEmployees, fetchProjectById, getDataMode } from '@/lib/data-service';
import type { ProjectFilterParams } from '@/lib/data-service';
import { PaginationControls } from '@/components/pagination-controls';
import { DEFAULT_PAGE_SIZE } from '@/types/pagination';
import type { PaginationMetadata, PaginationParams } from '@/types/pagination';
import {
  deriveProjectHealthWithBreakdown,
  getBudgetHealthThresholds,
} from '@/lib/budget-health-thresholds-storage';
import {
  formatCurrency,
  formatDate,
  healthToVariant,
} from '@/lib/utils';
import { PROJECT_STATUS_LABELS } from '@/types';
import type { Project, Client, Employee } from '@/types';
import { mockEmployees } from '@/lib/mock-data';
import { useErrorHandler } from '@/hooks/use-error-handler';
import { useCompany } from '@/contexts/CompanyContext';
import { useProjectStatuses } from '@/contexts/ProjectStatusContext';
import { ROUTES } from '@/routes';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

type Health = 'green' | 'amber' | 'red';

const HEALTH_ORDER: Record<Health, number> = { red: 0, amber: 1, green: 2 };

function healthLabel(h: Health): string {
  return h.charAt(0).toUpperCase() + h.slice(1);
}

function getPmName(project: Project): string {
  if (typeof project.projectManagerId === 'number') {
    const emp = mockEmployees.find(e => e.id === project.projectManagerId);
    if (emp) return emp.name;
  }
  if (typeof project.projectManagerId === 'string' && project.projectManagerId) {
    return project.projectManagerId;
  }
  return '—';
}

function getForecastDelayDays(project: Project): number | null {
  if (!project.forecastEndDate || !project.endDate) return null;
  const forecast = new Date(project.forecastEndDate).getTime();
  const planned = new Date(project.endDate).getTime();
  const diffMs = forecast - planned;
  if (diffMs <= 0) return null;
  return Math.round(diffMs / (1000 * 60 * 60 * 24));
}

function ForecastEndCell({ project }: { project: Project }) {
  if (!project.forecastEndDate) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-muted-foreground">—</span>
          </TooltipTrigger>
          <TooltipContent>PM has not set a forecast end date</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  const delayDays = getForecastDelayDays(project);
  const formatted = formatDate(project.forecastEndDate);
  if (delayDays === null) {
    return <span className="text-sm">{formatted}</span>;
  }
  const colorClass = delayDays > 28 ? 'text-destructive font-medium' : 'text-warning-700 font-medium';
  return (
    <span className={`text-sm ${colorClass}`}>
      {formatted}{' '}
      <span className="text-xs">(+{delayDays}d)</span>
    </span>
  );
}

function HealthBadge({ health, label }: { health: Health; label?: string }) {
  const variant = healthToVariant(health);
  return (
    <Badge variant={variant} className="text-xs gap-1.5">
      <StatusIndicator variant={variant === 'destructive' ? 'error' : variant} size="sm" />
      {label ?? healthLabel(health)}
    </Badge>
  );
}

function DualHealthCell({ pmHealth, systemHealth }: { pmHealth: Health; systemHealth: Health | null }) {
  if (!systemHealth) {
    return (
      <div className="flex items-center gap-1">
        <HealthBadge health={pmHealth} />
        <span className="text-xs text-muted-foreground">System: N/A</span>
      </div>
    );
  }
  if (pmHealth === systemHealth) {
    return <HealthBadge health={pmHealth} />;
  }
  return (
    <TooltipProvider>
      <div className="flex items-center gap-1">
        <HealthBadge health={pmHealth} label={`PM: ${healthLabel(pmHealth)}`} />
        <HealthBadge health={systemHealth} label={`Sys: ${healthLabel(systemHealth)}`} />
        <Tooltip>
          <TooltipTrigger asChild>
            <AlertTriangle className="h-3.5 w-3.5 text-warning-600 cursor-help shrink-0" />
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            PM assessment and system calculation disagree. System health is based on objective data signals.
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}

function StatusBadge({ statusId }: { statusId: number }) {
  const label = PROJECT_STATUS_LABELS[statusId] ?? String(statusId);
  const variantMap: Record<number, string> = {
    1: 'secondary',   // Planning
    2: 'default',     // In Progress
    3: 'outline',     // On Hold
    5: 'destructive', // Cancelled
    6: 'secondary',   // Completed
  };
  return (
    <Badge variant={(variantMap[statusId] as any) ?? 'outline'} className="text-xs whitespace-nowrap">
      {label}
    </Badge>
  );
}

function deriveSystemHealth(project: Project) {
  const thresholds = getBudgetHealthThresholds();
  try {
    const result = deriveProjectHealthWithBreakdown(
      {
        actualCost: (project.actualLabour ?? 0) + (project.actualExpense ?? 0),
        budgetCost: (project.budgetLabour ?? 0) + (project.budgetExpense ?? 0),
        startDate: project.startDate,
        endDate: project.endDate,
        completionPercent: project.completionPercentOverride ?? project.completionPercent ?? 0,
        actualHours: project.actualHours ?? 0,
        budgetHours: project.budgetHours ?? 0,
        wipAmount: project.wipAmount ?? 0,
        actualRevenue: project.actualRevenue ?? 0,
        budgetRevenue: project.budgetRevenue ?? 0,
        forecastEndDate: project.forecastEndDate,
        overdueMilestoneCount: 0,
      },
      thresholds
    );
    return result;
  } catch {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Mode 2 — Single Project Detail
// ─────────────────────────────────────────────────────────────────────────────

function Mode2View({
  project,
  onBack,
  backLabel,
  showBackButton,
}: {
  project: Project;
  onBack: () => void;
  backLabel: string;
  showBackButton: boolean;
}) {
  const navigate = useNavigate();
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const systemHealthResult = useMemo(() => deriveSystemHealth(project), [project]);
  const systemHealth = systemHealthResult?.health ?? null;
  const pmHealth = project.health as Health;

  const gp = (project.actualSales ?? 0) - (project.actualLabour ?? 0) - (project.actualExpense ?? 0);
  const gpPct =
    (project.actualSales ?? 0) > 0
      ? (gp / (project.actualSales ?? 1)) * 100
      : null;

  const delayDays = getForecastDelayDays(project);
  const pmName = getPmName(project);

  const typeLabels: Record<string, string> = {
    'time-and-materials': 'Time & Materials',
    'fixed-price': 'Fixed Price',
    retainer: 'Retainer',
  };

  const latestNote = project.noteLog?.[0];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full">
      {/* Top bar — only shown when navigated from Mode 1 (row click), not on deep-link */}
      {showBackButton && (
        <div className="lg:col-span-2 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={onBack} className="gap-2 -ml-2">
            <ArrowLeft className="h-4 w-4" />
            {backLabel}
          </Button>

        </div>
      )}

      {/* Identity header — full width */}
      <Card className="lg:col-span-2">
        <CardContent className="pt-6">
          <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
            Project Status Report
          </div>
          <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
            <div className="flex gap-2">
              <span className="text-muted-foreground w-24 shrink-0">Project</span>
              <span className="font-medium">{project.name}</span>
            </div>
            <div className="flex gap-2">
              <span className="text-muted-foreground w-24 shrink-0">Code</span>
              <span>{project.code ?? '—'}</span>
            </div>
            <div className="flex gap-2">
              <span className="text-muted-foreground w-24 shrink-0">Client</span>
              <span>{project.clientNames ?? '—'}</span>
            </div>
            <div className="flex gap-2">
              <span className="text-muted-foreground w-24 shrink-0">Type</span>
              <span>{typeLabels[project.type] ?? project.type ?? '—'}</span>
            </div>
            <div className="flex gap-2">
              <span className="text-muted-foreground w-24 shrink-0">PM</span>
              <span>{pmName}</span>
            </div>
            <div className="flex gap-2 items-center">
              <span className="text-muted-foreground w-24 shrink-0">Status</span>
              <StatusBadge statusId={project.statusId} />
            </div>
            <div className="flex gap-2 col-span-2">
              <span className="text-muted-foreground w-24 shrink-0">Period</span>
              <span>
                {formatDate(project.startDate)} → {formatDate(project.endDate)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Health */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">
            Health
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3 text-sm">
              <span className="text-muted-foreground w-36 shrink-0">PM Assessment</span>
              <HealthBadge health={pmHealth} />
            </div>
            {systemHealth && (
              <div className="flex items-center gap-3 text-sm">
                <span className="text-muted-foreground w-36 shrink-0">System Health</span>
                <HealthBadge health={systemHealth} />
              </div>
            )}
            {systemHealth && pmHealth !== systemHealth && (
              <div className="flex items-center gap-2 text-sm text-warning-700 bg-warning-50 border border-warning-200 rounded-md px-3 py-2">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>
                  Disagreement — PM assessment and system health differ. System health is based on
                  objective data signals.
                </span>
              </div>
            )}
          </div>

          {/* Breakdown collapsible */}
          {systemHealthResult && systemHealthResult.dimensions.length > 0 && (
            <Collapsible open={breakdownOpen} onOpenChange={setBreakdownOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground h-8 px-2">
                  {breakdownOpen ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                  System Health Breakdown
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="mt-2 flex flex-col gap-1.5 pl-2 border-l-2 border-muted ml-1">
                  {systemHealthResult.dimensions.map(dim => {
                    const dot =
                      dim.signal === 'green' ? '🟢' : dim.signal === 'amber' ? '🟡' : '🔴';
                    return (
                      <div key={dim.label} className="flex items-center gap-3 text-sm">
                        <span className="w-28 text-muted-foreground text-xs shrink-0">
                          {dim.label}
                        </span>
                        <span>{dot}</span>
                        <span className="text-muted-foreground text-xs">{dim.detail}</span>
                      </div>
                    );
                  })}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}
        </CardContent>
      </Card>

      {/* Schedule */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">
            Schedule
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 text-sm">
          <div className="grid grid-cols-2 gap-x-8 gap-y-2">
            <div className="flex gap-2">
              <span className="text-muted-foreground w-32 shrink-0">Planned Start</span>
              <span>{formatDate(project.startDate)}</span>
            </div>
            <div className="flex gap-2">
              <span className="text-muted-foreground w-32 shrink-0">Planned End</span>
              <span>{formatDate(project.endDate)}</span>
            </div>
            <div className="flex gap-2 items-start col-span-2">
              <span className="text-muted-foreground w-32 shrink-0">Forecast End</span>
              <div className="flex flex-col gap-1">
                {project.forecastEndDate ? (
                  <>
                    <span
                      className={
                        delayDays && delayDays > 28
                          ? 'text-destructive font-medium'
                          : delayDays
                          ? 'text-warning-700 font-medium'
                          : ''
                      }
                    >
                      {formatDate(project.forecastEndDate)}
                      {delayDays ? ` (+${delayDays} days)` : ''}
                    </span>
                    {delayDays && (
                      <span
                        className={`text-xs flex items-center gap-1 ${
                          delayDays > 28 ? 'text-destructive' : 'text-warning-700'
                        }`}
                      >
                        <AlertTriangle className="h-3 w-3" />
                        {delayDays} days late vs planned end
                      </span>
                    )}
                    {!delayDays && (
                      <span className="text-xs text-success-700">✓ On track</span>
                    )}
                  </>
                ) : (
                  <span className="text-muted-foreground">
                    Not set
                    <span className="text-xs ml-1">(PM has not set a forecast end date)</span>
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-1.5 mt-1">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Completion</span>
              <span>{project.completionPercent ?? 0}%</span>
            </div>
            <Progress value={project.completionPercent ?? 0} className="h-2" />
          </div>
        </CardContent>
      </Card>

      {/* Financial Summary — full width */}
      <Card className="lg:col-span-2">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">
            Financial Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
            <div className="flex gap-2">
              <span className="text-muted-foreground w-36 shrink-0">Contract Value</span>
              <SensitiveData>
                <span>{formatCurrency(project.budgetRevenue)}</span>
              </SensitiveData>
            </div>
            <div className="flex gap-2">
              <span className="text-muted-foreground w-36 shrink-0">Actual Revenue</span>
              <SensitiveData>
                <span>{formatCurrency(project.actualSales)}</span>
              </SensitiveData>
            </div>
            <div className="flex gap-2">
              <span className="text-muted-foreground w-36 shrink-0">Actual Cost</span>
              <SensitiveData>
                <span>
                  {formatCurrency((project.actualLabour ?? 0) + (project.actualExpense ?? 0))}
                </span>
              </SensitiveData>
            </div>
            <div className="flex gap-2">
              <span className="text-muted-foreground w-36 shrink-0">Gross Profit</span>
              <SensitiveData>
                <span className={gp < 0 ? 'text-destructive' : ''}>{formatCurrency(gp)}</span>
              </SensitiveData>
            </div>
            <div className="flex gap-2">
              <span className="text-muted-foreground w-36 shrink-0">Gross Margin</span>
              <SensitiveData>
                <span
                  className={
                    gpPct === null ? '' : gpPct < 0 ? 'text-destructive' : ''
                  }
                >
                  {gpPct !== null ? `${gpPct.toFixed(1)}%` : '—'}
                </span>
              </SensitiveData>
            </div>
            <div className="flex gap-2">
              <span className="text-muted-foreground w-36 shrink-0">Hours</span>
              <span>
                Budget: {(project.budgetHours ?? 0).toLocaleString()} hrs &nbsp;·&nbsp; Actual:{' '}
                {(project.actualHours ?? 0).toLocaleString()} hrs
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Objectives — hidden if empty, full width */}
      {project.objectives && (
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">
              Objectives
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-line">{project.objectives}</p>
          </CardContent>
        </Card>
      )}

      {/* Key Deliverables — hidden if empty, full width */}
      {project.keyDeliverables && (
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">
              Key Deliverables
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-line">{project.keyDeliverables}</p>
          </CardContent>
        </Card>
      )}

      {/* Latest Update — hidden if no notes, full width */}
      {latestNote && (
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">
              Latest Update
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            <p className="text-sm italic">"{latestNote.content}"</p>
            <p className="text-xs text-muted-foreground">
              — {latestNote.createdByName},{' '}
              {formatDate(latestNote.createdAt)}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Footer links — full width */}
      <div className="lg:col-span-2 flex items-center gap-3 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={() => navigate(`${ROUTES.BUDGET_VARIANCE_REPORT}?reportProjectId=${project.id}`)}
        >
          Budget Variance
          <ArrowRight className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={() => navigate(`${ROUTES.PROJECT_FORECAST_REPORT}?reportProjectId=${project.id}`)}
        >
          Project Forecast
          <ArrowRight className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={() => navigate(ROUTES.PROJECT_DETAILS(project.id))}
        >
          Open Project
          <ArrowRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Mode 1 — Portfolio Table
// ─────────────────────────────────────────────────────────────────────────────

// Column definitions for the filter sheet (identifies which columns are filterable)
const REPORT_COLUMN_DEFS: ColumnDefinition[] = [
  { id: 'health-indicator', name: 'Health',        description: 'Project health status', category: 'Status',     visible: true, order: 1 },
  { id: 'project-name',     name: 'Project',       description: 'Project name',          category: 'Status',     visible: true, order: 2 },
  { id: 'client-name',      name: 'Client',        description: 'Client name',           category: 'Client',     visible: true, order: 3 },
  { id: 'project-manager',  name: 'PM',            description: 'Project manager',       category: 'Status',     visible: true, order: 4 },
  { id: 'project-status',   name: 'Status',        description: 'Project status',        category: 'Status',     visible: true, order: 5 },
  { id: 'project-type',     name: 'Project Type',  description: 'Project type',          category: 'Status',     visible: true, order: 6 },
];

type EnrichedProject = Project & {
  systemHealth: Health | null;
  pmName: string;
  gpPct: number | null;
};

function Mode1View({
  projects,
  clients,
  employees,
  onSelectProject,
  apiHealthSummary,
  onRefetch,
  pagination,
  initialPmId = null,
}: {
  projects: Project[];
  clients: Client[];
  employees: Employee[];
  onSelectProject: (id: number) => void;
  apiHealthSummary?: { greenCount: number; amberCount: number; redCount: number } | null;
  onRefetch?: (filters: ProjectFilterParams, paginationParams?: PaginationParams) => void;
  pagination?: PaginationMetadata | null;
  /** Pre-filter to a specific PM — passed from the homepage quick link via ?managerId= */
  initialPmId?: number | null;
}) {
  const { statusLabels } = useProjectStatuses();
  const isLive = getDataMode() === 'live';

  // ── State ──────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm]           = useState('');
  const [columnSorts, setColumnSorts]         = useState<ColumnSort[]>([{ columnId: 'health', direction: 'asc' }]);
  // Pre-populate the filter sheet with the PM filter if arriving from the homepage quick link
  const [columnFilters, setColumnFilters]     = useState<ColumnFilters>(() =>
    initialPmId
      ? { 'project-manager': { kind: 'multi-select', selected: [String(initialPmId)] } } as ColumnFilters
      : {} as ColumnFilters
  );
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [isExporting, setIsExporting]         = useState(false);
  const [isFetching, setIsFetching]           = useState(false);

  // Summary card health quick-filter
  const [healthCardFilter, setHealthCardFilter] = useState<Health | 'all'>('all');

  // Inline toolbar filters — matching Projects List pattern
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [healthFilter, setHealthFilter] = useState<Health | 'all'>('all');

  // PM filter — initialised from URL param when arriving via homepage quick link
  const [pmIdFilter, setPmIdFilter] = useState<number | null>(initialPmId ?? null);

  // Keep pmIdFilter in sync with the filter sheet's project-manager column filter
  // so clearing it in the sheet also clears the server-side fetch param
  useEffect(() => {
    const sheetPmFilter = columnFilters['project-manager'];
    if (sheetPmFilter && sheetPmFilter.kind === 'multi-select' && sheetPmFilter.selected.length > 0) {
      setPmIdFilter(Number(sheetPmFilter.selected[0]));
    } else {
      setPmIdFilter(null);
    }
  }, [columnFilters]);

  const thresholds = useMemo(() => getBudgetHealthThresholds(), []);

  // Build current filter params (used by both filter-change and page-change)
  const buildFilters = useCallback((): ProjectFilterParams => {
    const filters: ProjectFilterParams = {};
    if (statusFilter !== 'all') filters.statusId = Number(statusFilter);
    const effectiveHealth = healthCardFilter !== 'all' ? healthCardFilter : healthFilter !== 'all' ? healthFilter : undefined;
    if (effectiveHealth) filters.health = effectiveHealth;
    if (searchTerm.trim()) filters.search = searchTerm.trim();
    if (pmIdFilter != null) filters.pmId = pmIdFilter;
    return filters;
  }, [statusFilter, healthCardFilter, healthFilter, searchTerm, pmIdFilter]);

  // Keep a ref to the latest filters so page-change handlers can read them
  const filtersRef = useRef<ProjectFilterParams>({});
  filtersRef.current = buildFilters();

  // ── Server-side refetch when filters change (live mode only) ─────
  // Resets to page 1 on every filter change.
  useEffect(() => {
    if (!isLive || !onRefetch) return;
    const handler = setTimeout(() => {
      setIsFetching(true);
      Promise.resolve(onRefetch(filtersRef.current)).finally(() => setIsFetching(false));
    }, 300);
    return () => clearTimeout(handler);
  }, [isLive, onRefetch, statusFilter, healthCardFilter, healthFilter, searchTerm, pmIdFilter]);

  // ── Pagination handlers (live mode only) ───────────────────────────
  const handlePageChange = useCallback((page: number) => {
    if (!onRefetch || !pagination) return;
    setIsFetching(true);
    Promise.resolve(onRefetch(filtersRef.current, { page, pageSize: pagination.pageSize }))
      .finally(() => setIsFetching(false));
  }, [onRefetch, pagination]);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    if (!onRefetch) return;
    setIsFetching(true);
    Promise.resolve(onRefetch(filtersRef.current, { page: 1, pageSize }))
      .finally(() => setIsFetching(false));
  }, [onRefetch]);

  // ── Enrich projects with derived fields ────────────────────────────
  const enriched = useMemo<EnrichedProject[]>(() =>
    projects.map(p => {
      const sysResult = (() => {
        try {
          return deriveProjectHealthWithBreakdown(
            {
              actualCost: (p.actualLabour ?? 0) + (p.actualExpense ?? 0),
              budgetCost: (p.budgetLabour ?? 0) + (p.budgetExpense ?? 0),
              startDate: p.startDate,
              endDate: p.endDate,
              completionPercent: p.completionPercentOverride ?? p.completionPercent ?? 0,
              actualHours: p.actualHours ?? 0,
              budgetHours: p.budgetHours ?? 0,
              wipAmount: p.wipAmount ?? 0,
              actualRevenue: p.actualRevenue ?? 0,
              budgetRevenue: p.budgetRevenue ?? 0,
              forecastEndDate: p.forecastEndDate,
              overdueMilestoneCount: 0,
            },
            thresholds,
          );
        } catch { return null; }
      })();
      const pmName = getPmName(p);
      const gp     = (p.actualSales ?? 0) - (p.actualLabour ?? 0) - (p.actualExpense ?? 0);
      const gpPct  = (p.actualSales ?? 0) > 0 ? (gp / (p.actualSales ?? 1)) * 100 : null;
      return { ...p, systemHealth: sysResult?.health ?? null, pmName, gpPct };
    }),
    [projects, thresholds],
  );

  // ── Dynamic filter options ─────────────────────────────────────────
  const availableOptions = useMemo(() => {
    const pmNames     = [...new Set(employees.map(e => e.name).filter(Boolean))].sort();
    const clientNames = [...new Set(clients.map(c => c.name).filter(Boolean))].sort();
    return {
      'project-manager': pmNames.map(n  => ({ value: n, label: n })),
      'client-name':     clientNames.map(n => ({ value: n, label: n })),
    };
  }, [employees, clients]);

  // ── Filter + search + health card ─────────────────────────────────
  // In live mode the API already applies status/health/search filters,
  // so we only apply column-sheet filters and sorting client-side.
  // In demo/tour modes we still filter everything client-side.
  const filtered = useMemo(() => {
    let rows: EnrichedProject[] = applyColumnFilters(enriched, columnFilters, { clients }) as EnrichedProject[];

    if (!isLive) {
      if (healthCardFilter !== 'all') {
        rows = rows.filter(p => p.health === healthCardFilter);
      }

      if (statusFilter !== 'all') {
        rows = rows.filter(p => String(p.statusId) === statusFilter);
      }

      if (healthFilter !== 'all') {
        rows = rows.filter(p => p.health === healthFilter);
      }

      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        rows = rows.filter(p =>
          p.name.toLowerCase().includes(q) ||
          (p.code ?? '').toLowerCase().includes(q) ||
          (p.clientNames ?? '').toLowerCase().includes(q) ||
          p.pmName.toLowerCase().includes(q),
        );
      }
    }

    return rows;
  }, [enriched, columnFilters, healthCardFilter, statusFilter, healthFilter, searchTerm, isLive, clients]);

  // ── Summary card counts ──
  // When available, use API-provided health summary (covers the entire filtered
  // dataset, not just the current page). Fall back to client-side counts.
  const summary = useMemo(() => {
    if (apiHealthSummary) {
      const total = apiHealthSummary.greenCount + apiHealthSummary.amberCount + apiHealthSummary.redCount;
      return { total, green: apiHealthSummary.greenCount, amber: apiHealthSummary.amberCount, red: apiHealthSummary.redCount };
    }
    const base  = applyColumnFilters(enriched, columnFilters, { clients }) as EnrichedProject[];
    const green = base.filter(p => p.health === 'green').length;
    const amber = base.filter(p => p.health === 'amber').length;
    const red   = base.filter(p => p.health === 'red').length;
    return { total: base.length, green, amber, red };
  }, [enriched, columnFilters, apiHealthSummary, clients]);

  // ── Multi-column sort ──────────────────────────────────────────────
  const sorted = useMemo(() => {
    if (columnSorts.length === 0) return filtered;
    const arr = [...filtered];
    arr.sort((a, b) => {
      for (const { columnId, direction } of columnSorts) {
        let cmp = 0;
        switch (columnId) {
          case 'health':        cmp = HEALTH_ORDER[a.health as Health] - HEALTH_ORDER[b.health as Health]; break;
          case 'name':          cmp = a.name.localeCompare(b.name); break;
          case 'client':        cmp = (a.clientNames ?? '').localeCompare(b.clientNames ?? ''); break;
          case 'pm':            cmp = a.pmName.localeCompare(b.pmName); break;
          case 'status':        cmp = (PROJECT_STATUS_LABELS[a.statusId] ?? '').localeCompare(PROJECT_STATUS_LABELS[b.statusId] ?? ''); break;
          case 'completion':    cmp = (a.completionPercent ?? 0) - (b.completionPercent ?? 0); break;
          case 'endDate':       cmp = (a.endDate ?? '').localeCompare(b.endDate ?? ''); break;
          case 'forecastEnd':   cmp = (a.forecastEndDate ?? '').localeCompare(b.forecastEndDate ?? ''); break;
          case 'gp':            cmp = (a.gpPct ?? -Infinity) - (b.gpPct ?? -Infinity); break;
          case 'contractValue': cmp = (a.budgetRevenue ?? 0) - (b.budgetRevenue ?? 0); break;
        }
        if (cmp !== 0) return direction === 'asc' ? cmp : -cmp;
      }
      return a.name.localeCompare(b.name);
    });
    return arr;
  }, [filtered, columnSorts]);

  const activeFilterCount = countActiveFilters(columnFilters);
  const hasActiveFilters  = activeFilterCount > 0 || healthCardFilter !== 'all' || statusFilter !== 'all' || healthFilter !== 'all' || searchTerm.trim() !== '';

  function clearAll() {
    setSearchTerm('');
    setColumnFilters({});
    setHealthCardFilter('all');
    setStatusFilter('all');
    setHealthFilter('all');
  }

  // ── Excel export ───────────────────────────────────────────────────
  const handleExportExcel = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);
    toast.info('Export started — your download will begin shortly.');

    try {
      const ref: ExportReferenceData = { clients, employees, tasks: [], milestones: [], resourceAllocations: [] };

      if (!isLive) {
        await exportProjectsToExcel(sorted, REPORT_COLUMN_DEFS, ref, statusLabels);
        toast.success('Export complete — file downloaded.');
        return;
      }

      const allProjects = await fetchAllProjectPages('project-status-report', filtersRef.current);
      await exportProjectsToExcel(allProjects, REPORT_COLUMN_DEFS, ref, statusLabels);
      toast.success(`Export complete — ${allProjects.length} projects downloaded.`);
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, isLive, sorted, clients, employees, statusLabels]);

  // ── TanStack column definitions ────────────────────────────────────
  const columns = useMemo<ColumnDef<EnrichedProject, unknown>[]>(() => [
    {
      id: 'health',
      header: 'Health',
      cell: ({ row }) => (
        <DualHealthCell
          pmHealth={row.original.health as Health}
          systemHealth={row.original.systemHealth}
        />
      ),
    },
    {
      id: 'name',
      header: 'Project',
      cell: ({ row }) => (
        <div>
          <div className="font-medium text-sm">{row.original.name}</div>
          {row.original.code && (
            <div className="text-xs text-muted-foreground">{row.original.code}</div>
          )}
        </div>
      ),
    },
    {
      id: 'client',
      header: 'Client',
      cell: ({ row }) => (
        <span className="text-sm">{row.original.clientNames ?? '—'}</span>
      ),
    },
    {
      id: 'pm',
      header: 'Project Manager',
      cell: ({ row }) => (
        <span className="text-sm whitespace-nowrap">{row.original.pmName}</span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => <StatusBadge statusId={row.original.statusId} />,
    },
    {
      id: 'completion',
      header: () => <span className="block text-right">Complete</span>,
      cell: ({ row }) => (
        <div className="flex flex-col items-end gap-1 min-w-[80px]">
          <span className="text-xs">{row.original.completionPercent ?? 0}%</span>
          <Progress value={row.original.completionPercent ?? 0} className="h-1.5 w-16" />
        </div>
      ),
    },
    {
      id: 'endDate',
      header: 'Planned End',
      cell: ({ row }) => (
        <span className="text-sm whitespace-nowrap">{formatDate(row.original.endDate)}</span>
      ),
    },
    {
      id: 'forecastEnd',
      header: 'Forecast End',
      cell: ({ row }) => <ForecastEndCell project={row.original} />,
    },
    {
      id: 'gp',
      header: () => <span className="block text-right">GP%</span>,
      cell: ({ row }) => {
        const gpPct = row.original.gpPct;
        return (
          <div className="text-right">
            <SensitiveData>
              <span className={`text-sm ${gpPct === null ? 'text-muted-foreground' : gpPct < 0 ? 'text-destructive' : ''}`}>
                {gpPct !== null ? `${gpPct.toFixed(1)}%` : '—'}
              </span>
            </SensitiveData>
          </div>
        );
      },
    },
    {
      id: 'contractValue',
      header: () => <span className="block text-right">Contract Value</span>,
      cell: ({ row }) => (
        <div className="text-right">
          <SensitiveData>
            <span className="text-sm whitespace-nowrap">
              {formatCurrency(row.original.budgetRevenue)}
            </span>
          </SensitiveData>
        </div>
      ),
    },
    {
      id: 'billable',
      header: 'Billable',
      cell: ({ row }) => {
        const isBillable = row.original.billable ?? true;
        return isBillable
          ? <Badge variant="outline" className="text-xs text-emerald-700 bg-emerald-50 border-emerald-200">Billable</Badge>
          : <Badge variant="outline" className="text-xs text-muted-foreground">Non-billable</Badge>;
      },
    },
  ], []);

  return (
    <div className="flex flex-col gap-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {/* Total Projects */}
        <CompactKpiCard
          title="Total Projects"
          value={summary.total}
          description="All active projects"
          icon={LayoutGrid}
          accentVariant="info"
          className="shadow-none"
        />

        {/* Green — clickable filter */}
        <div
          className={`cursor-pointer transition-all rounded-xl ${healthCardFilter === 'green' ? 'ring-2 ring-offset-2 ring-success-400' : ''}`}
          onClick={() => setHealthCardFilter(h => h === 'green' ? 'all' : 'green')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setHealthCardFilter(h => h === 'green' ? 'all' : 'green'); } }}
          title={healthCardFilter === 'green' ? 'Click to remove filter' : 'Click to filter green projects'}
        >
          <CompactKpiCard
            title="Green"
            value={summary.green}
            description={healthCardFilter === 'green' ? 'Click to remove filter' : 'Click to filter'}
            icon={CircleCheck}
            accentVariant="success"
            className="h-full shadow-none"
          />
        </div>

        {/* Amber — clickable filter */}
        <div
          className={`cursor-pointer transition-all rounded-xl ${healthCardFilter === 'amber' ? 'ring-2 ring-offset-2 ring-warning-400' : ''}`}
          onClick={() => setHealthCardFilter(h => h === 'amber' ? 'all' : 'amber')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setHealthCardFilter(h => h === 'amber' ? 'all' : 'amber'); } }}
          title={healthCardFilter === 'amber' ? 'Click to remove filter' : 'Click to filter amber projects'}
        >
          <CompactKpiCard
            title="Amber"
            value={summary.amber}
            description={healthCardFilter === 'amber' ? 'Click to remove filter' : 'Click to filter'}
            icon={TriangleAlert}
            accentVariant="warning"
            className="h-full shadow-none"
          />
        </div>

        {/* Red — clickable filter */}
        <div
          className={`cursor-pointer transition-all rounded-xl ${healthCardFilter === 'red' ? 'ring-2 ring-offset-2 ring-destructive/60' : ''}`}
          onClick={() => setHealthCardFilter(h => h === 'red' ? 'all' : 'red')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setHealthCardFilter(h => h === 'red' ? 'all' : 'red'); } }}
          title={healthCardFilter === 'red' ? 'Click to remove filter' : 'Click to filter red projects'}
        >
          <CompactKpiCard
            title="Red"
            value={summary.red}
            description={healthCardFilter === 'red' ? 'Click to remove filter' : 'Click to filter'}
            icon={CircleX}
            accentVariant="error"
            className="h-full shadow-none"
          />
        </div>
      </div>

      {/* Toolbar — matches Projects List pattern */}
      <Card data-tour="project-status-filters">
        <CardContent className="p-0">
          <div className="flex gap-2 p-6 pb-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by project code, name, client or PM..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-9 pr-9"
              />
              {searchTerm && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>

            {/* Status inline filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {Object.entries(PROJECT_STATUS_LABELS).map(([id, label]) => (
                  <SelectItem key={id} value={id}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Health inline filter */}
            <Select value={healthFilter} onValueChange={v => setHealthFilter(v as Health | 'all')}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All Health" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Health</SelectItem>
                <SelectItem value="green">Green</SelectItem>
                <SelectItem value="amber">Amber</SelectItem>
                <SelectItem value="red">Red</SelectItem>
              </SelectContent>
            </Select>

            {/* Clear all filters */}
            {hasActiveFilters && (
              <Button variant="outline" onClick={clearAll} className="text-muted-foreground gap-2">
                <X className="h-4 w-4" />
                Clear Filters
              </Button>
            )}

            {/* Filter sheet — advanced column filters */}
            <Button
              variant="outline"
              onClick={() => setFilterSheetOpen(true)}
              className="h-9 w-9 p-0 relative"
              aria-label="Filter"
            >
              <Filter className="h-4 w-4" />
              {activeFilterCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 text-[10px] font-semibold text-primary-foreground min-w-[18px] h-[18px] rounded-full bg-primary inline-flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </Button>

            {/* Export to Excel */}
            <Button
              variant="outline"
              onClick={handleExportExcel}
              disabled={isExporting}
              className="h-9 w-9 p-0"
              aria-label="Export to Excel"
              title="Export to Excel"
            >
              {isExporting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* DataTableEnhanced */}
          <div className="relative" data-tour="project-status-table">
            {isFetching && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/60 backdrop-blur-[1px] rounded-md">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}
            <DataTableEnhanced
              columns={columns}
              data={sorted}
              columnSorts={columnSorts}
              onColumnSortChange={setColumnSorts}
              onRowClick={row => onSelectProject(row.id)}
              getRowId={row => String(row.id)}
              className="rounded-none border-0 border-t"
              emptyMessage={
                hasActiveFilters ? (
                  <span>
                    No projects match the selected filters.{' '}
                    <button className="underline hover:no-underline" onClick={clearAll}>
                      Clear filters
                    </button>
                  </span>
                ) : (
                  'No projects found.'
                )
              }
              paginationContent={
                isLive && pagination && pagination.totalItems > 0 ? (
                  <PaginationControls
                    pagination={pagination}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    showPageSizeSelector
                  />
                ) : null
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* Filter sheet */}
      <ProjectFilterSheet
        open={filterSheetOpen}
        onOpenChange={setFilterSheetOpen}
        visibleColumns={REPORT_COLUMN_DEFS}
        filters={columnFilters}
        onApply={setColumnFilters}
        onClear={() => setColumnFilters({})}
        availableOptions={availableOptions}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────

export function ProjectStatusReportPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { handleApiError } = useErrorHandler({ useToast: true });
  const { currentCompany } = useCompany();

  const [loading, setLoading] = useState(true);
  const [projects, setProjects]   = useState<Project[]>([]);
  const [clients, setClients]     = useState<Client[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [apiHealthSummary, setApiHealthSummary] = useState<{ greenCount: number; amberCount: number; redCount: number } | null>(null);
  const [pagination, setPagination] = useState<PaginationMetadata | null>(null);

  // Initialise from URL params synchronously so Mode 2 is shown immediately on deep-link
  const initProjectIdParam = searchParams.get('reportProjectId');
  const initModeParam = searchParams.get('mode');
  const initProjectId = initProjectIdParam && initModeParam === 'detail'
    ? Number(initProjectIdParam)
    : null;

  // Pre-filter to a specific PM when arriving via the homepage quick link (?managerId=123)
  const initManagerIdParam = searchParams.get('managerId');
  const initManagerId = initManagerIdParam ? Number(initManagerIdParam) : null;

  // selectedProjectId: null = Mode 1, number = Mode 2
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(initProjectId);

  // Track whether Mode 2 was entered from Mode 1 (row click) or direct deep-link
  const [enteredFromMode1, setEnteredFromMode1] = useState(false);

  // Deep-linked project fetched by ID (may not be in the paginated list)
  const [deepLinkedProject, setDeepLinkedProject] = useState<Project | null>(null);

  useEffect(() => {
    if (initProjectId != null) {
      fetchProjectById(initProjectId).then((p) => {
        if (p) setDeepLinkedProject(p as Project);
      }).catch(() => { /* ignore — will fall back to paginated list */ });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Saved filter state for "Back to Portfolio" preservation
  const [savedFilters] = useState<null>(null); // reserved — filters are in Mode1View state

  // Shared helper to extract projects + pagination + summary from API response
  const applyProjectResult = useCallback((projectResult: any) => {
    if (projectResult && typeof projectResult === 'object' && 'data' in projectResult) {
      setProjects(projectResult.data as Project[]);
      if (projectResult.pagination) setPagination(projectResult.pagination);
      if (projectResult.summary) setApiHealthSummary(projectResult.summary);
    } else {
      const list: Project[] = Array.isArray(projectResult) ? projectResult : [];
      setProjects(list);
      const summary = (projectResult as any)?._summary ?? null;
      if (summary) setApiHealthSummary(summary);
    }
  }, []);

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        const [projectResult, clientList, employeeList] = await Promise.all([
          fetchProjects(
            'project-status-report',
            { page: 1, pageSize: DEFAULT_PAGE_SIZE },
            initManagerId ? { pmId: initManagerId } : undefined
          ),
          fetchClients('project-status-report'),
          fetchEmployees('project-status-report'),
        ]);
        applyProjectResult(projectResult);
        setClients(clientList as Client[]);
        setEmployees(employeeList as Employee[]);
      } catch (err) {
        handleApiError(err, 'Failed to load projects');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [currentCompany?.id]);

  // Re-fetch projects with server-side filters + pagination (called by Mode1View)
  const handleRefetch = useCallback(async (filters: ProjectFilterParams, paginationParams?: PaginationParams) => {
    try {
      const effectivePagination = paginationParams ?? { page: 1, pageSize: pagination?.pageSize ?? DEFAULT_PAGE_SIZE };
      const projectResult = await fetchProjects('project-status-report', effectivePagination, filters);
      applyProjectResult(projectResult);
    } catch (err) {
      handleApiError(err, 'Failed to filter projects');
    }
  }, [applyProjectResult, handleApiError, pagination?.pageSize]);

  function handleSelectProject(id: number) {
    setSelectedProjectId(id);
    setEnteredFromMode1(true);
    setSearchParams({ reportProjectId: String(id) });
  }

  function handleBack() {
    setSelectedProjectId(null);
    if (enteredFromMode1) {
      // Return to Mode 1 — clear URL params
      setSearchParams({});
    } else {
      // Deep-linked from project record — go back to that project's page
      const projectIdParam = searchParams.get('reportProjectId');
      if (projectIdParam) {
        navigate(-1);
      } else {
        setSearchParams({});
      }
    }
  }

  const selectedProject = selectedProjectId != null
    ? projects.find(p => p.id === selectedProjectId) ?? deepLinkedProject ?? null
    : null;

  if (loading) {
    return (
      <div className="p-6 flex flex-col gap-4">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-4 gap-4">
          {[0, 1, 2, 3].map(i => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-0">
      {selectedProjectId === null && (
        <PageHeaderBlock
          title="Project Status Report"
          subtitle="Portfolio-wide project health, schedule, and financial summary."
        />
      )}
      <div className="p-6">
        {selectedProject ? (
          <Mode2View
            project={selectedProject}
            onBack={handleBack}
            backLabel="Back to Portfolio"
            showBackButton={enteredFromMode1}
          />
        ) : (
          <Mode1View
            projects={projects}
            clients={clients}
            employees={employees}
            onSelectProject={handleSelectProject}
            apiHealthSummary={apiHealthSummary}
            onRefetch={handleRefetch}
            pagination={pagination}
            initialPmId={initManagerId}
          />
        )}
      </div>
    </div>
  );
}
